import { RefObject, useEffect } from 'react';

/**
 * What Tab may reach inside the dialog. Disabled controls are left out because the browser skips them
 * anyway — a trap that cycled onto one would strand the focus ring on a dead control.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The behaviour a modal dialog owes its user, for a dialog built out of plain elements: Escape closes
 * it, Tab stays inside it, focus starts in it and goes back where it came from, and the page behind
 * it does not scroll.
 *
 * The trap is the part `aria-modal` does not do: it tells assistive tech the rest of the page is
 * inert, but the browser still tabs a sighted keyboard user straight through the overlay into
 * whatever is behind it.
 *
 * Kept out of the components so the markup stays markup, and so this is testable on its own — none of
 * it is visible in a render tree.
 */
export const useDialogBehavior = (dialogRef: RefObject<HTMLElement>, onClose: VoidFunction): void => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const dialog = dialogRef.current;

      if (event.key === 'Escape') {
        onClose();

        return;
      }

      if (event.key !== 'Tab' || !dialog) {
        return;
      }

      const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));

      if (!focusable.length) {
        // Nothing to land on, so the dialog itself keeps the focus rather than letting it escape.
        event.preventDefault();
        dialog.focus();

        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const isLeavingForward = !event.shiftKey && active === last;
      // Shift+Tab off the first control — or off the dialog itself, which still holds the focus it
      // took on open — wraps round to the end.
      const isLeavingBackward = event.shiftKey && (active === first || active === dialog);

      if (isLeavingForward || isLeavingBackward || !dialog.contains(active)) {
        event.preventDefault();
        (isLeavingBackward ? last : first).focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dialogRef, onClose]);

  // The previous overflow is put back rather than a fixed value: the map view locks the body itself,
  // so resetting to `initial` would let the page scroll behind the map once the dialog closes.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // A dialog rendered at the end of the body is otherwise a whole page of tabbing away.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;

    dialogRef.current?.focus();

    return () => previouslyFocused?.focus();
  }, [dialogRef]);
};
